import { App } from '../App';

// Tile size. DEM tiles@2x are 512x512.
const TILE_SIZE = 512;

// Fixed zoom level for high-resolution elevation queries
const ZOOM_LEVEL = 14;

/**
 * Service for detecting whether a geographic point is on land or water
 * using Mapbox's Digital Elevation Model.
 *
 * Uses a singleton pattern with cached tiles to minimize network requests.
 */
class LandDetector {
  private static instance: LandDetector | null = null;
  private cache: { [key: string]: Uint8ClampedArray } = {};
  private pendingRequests: { [key: string]: Promise<Uint8ClampedArray> } = {};

  private constructor() {}

  /**
   * Get the singleton instance of LandDetector.
   */
  static getInstance(): LandDetector {
    if (!LandDetector.instance) {
      LandDetector.instance = new LandDetector();
    }
    return LandDetector.instance;
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = reject;
    });
  }

  private async urlToCanvas(url: string): Promise<Uint8ClampedArray> {
    const img = await this.loadImage(url);
    const bmp = await createImageBitmap(img);
    const canvas = document.createElement('canvas');
    canvas.width = TILE_SIZE;
    canvas.height = TILE_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("Failed to obtain 2D canvas context.");
    ctx.drawImage(bmp, 0, 0);
    return ctx.getImageData(0, 0, TILE_SIZE, TILE_SIZE).data;
  }

  /**
   * Convert RGB value to elevation in meters.
   * See https://docs.mapbox.com/data/tilesets/reference/mapbox-terrain-dem-v1/#elevation-data
   */
  private readElevation(data: Uint8ClampedArray, pixelX: number, pixelY: number): number {
    const offset = pixelY * TILE_SIZE * 4 + pixelX * 4;
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];
    return -10000 + ((r * 256 * 256 + g * 256 + b) * 0.1);
  }

  /**
   * Query elevation at a geographic coordinate.
   * @param lng Longitude in degrees
   * @param lat Latitude in degrees
   * @returns Elevation in meters (positive = above sea level, negative = below)
   */
  async queryElevation(lng: number, lat: number): Promise<number> {
    const numTiles = Math.pow(2, ZOOM_LEVEL);

    // Convert longitude to tileX, pixelX
    const hor = (lng + 180) / 360 * numTiles;
    const tileX = Math.floor(hor);
    const pixelX = Math.floor((hor - tileX) * TILE_SIZE);

    // Convert latitude to tileY, pixelY (Web Mercator projection)
    const vert = (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * numTiles;
    const tileY = Math.floor(vert);
    const pixelY = Math.floor((vert - tileY) * TILE_SIZE);

    const key = `${ZOOM_LEVEL}-${tileX}-${tileY}`;

    // If tile is in cache, use it
    if (this.cache[key]) {
      return this.readElevation(this.cache[key], pixelX, pixelY);
    }

    // If tile is being fetched, wait for it
    if (key in this.pendingRequests) {
      const data = await this.pendingRequests[key];
      return this.readElevation(data, pixelX, pixelY);
    }

    // Fetch the tile
    const url = `https://api.mapbox.com/v4/mapbox.terrain-rgb/${ZOOM_LEVEL}/${tileX}/${tileY}@2x.pngraw?access_token=${App.config.mapboxKey}`;

    this.pendingRequests[key] = this.urlToCanvas(url);

    try {
      const data = await this.pendingRequests[key];
      this.cache[key] = data;
      delete this.pendingRequests[key];
      return this.readElevation(data, pixelX, pixelY);
    } catch (error) {
      delete this.pendingRequests[key];
      throw error;
    }
  }

  /**
   * Check if a geographic point is on land.
   * Mapbox DEM returns exactly 0 for ocean/water areas.
   * @param lng Longitude in degrees
   * @param lat Latitude in degrees
   * @returns true if the point is on land (elevation > 0)
   */
  async isLand(lng: number, lat: number, threshold: number): Promise<boolean> {
    try {
      const elevation = await this.queryElevation(lng, lat);
      return elevation >= threshold;
    } catch {
      // If we can't determine, assume water (don't block layer values)
      return false;
    }
  }

  /**
   * Get the current cache size.
   */
  getCacheSize(): number {
    return Object.keys(this.cache).length;
  }

  /**
   * Clear the tile cache.
   */
  clearCache(): void {
    this.cache = {};
  }
}

export { LandDetector };
